import { Controller } from '@hotwired/stimulus'

// reCAPTCHA v3 を Turbo Drive 環境で確実に動作させるコントローラ。
//
// Devise のログイン/新規登録フォームでは `recaptcha_v3(script: false)` で hidden input のみを
// 描画し、api.js の読み込みとトークン生成をこのコントローラで制御する。
//
// これにより以下を実現する:
// - Turbo 遷移時にインラインスクリプトが実行されず「grecaptcha is not defined」になる問題を回避
//   （api.js を head に読み込み、window.grecaptcha が利用可能になってから実行する）
// - フォーム送信を `submit` でインターセプトし、送信直前に新しいトークンを生成して付与する
//   （非同期生成が間に合わずトークン未設定のまま送信されるレース条件を防止し、
//   かつ使い捨てトークンを毎回作り直す reCAPTCHA v3 の正しい使い方に沿う）
// - Turbo 遷移で破棄された reCAPTCHA を再初期化する（下記 isGrecaptchaReady のコメント参照）
// - トークン取得が遅延・失敗してもフォーム送信をブロックし続けない（タイムアウトで送信を続行）
interface GrecaptchaV3 {
  ready(callback: () => void): void
  execute(siteKey: string, options: { action: string }): Promise<string>
}

const API_SRC = 'https://www.google.com/recaptcha/api.js'
// 送信ボタンを押してからトークンを得るまでの合計予算。
// api.js の読み込み待ちと grecaptcha.execute() の両方を含む一本のタイマーであり、
// LOAD_TIMEOUT_MS が内側で独立して効くわけではない点に注意。
// 超過した場合は空トークンのまま送信を続行する（サーバー側で検証失敗として扱われる）。
const TOKEN_TIMEOUT_MS = 12000
// api.js の初期化完了を待つ最大時間。connect() の事前読み込み時点から計測されるため、
// 通常は送信までに読み込みが済んでおり、上の合計予算はほぼ execute() に使える。
const LOAD_TIMEOUT_MS = 8000
// api.js が document.body に挿入する reCAPTCHA バッジ。初期化完了の判定に使う。
const BADGE_SELECTOR = '.grecaptcha-badge'
// 「このバッジは今のドキュメントで生きている実体である」ことを示す目印。
// Turbo のスナップショットキャッシュ（戻る/進む）は body を cloneNode(true) するため
// バッジのクローンが復元されるが、cloneNode は JS プロパティを複製しないので
// クローンにはこの目印が付かない。DOM属性ではなくプロパティである点が本質。
// テストから同じ目印を立てられるようエクスポートする。
export const BADGE_LIVE_FLAG = '__recaptchaBadgeLive'

type FlaggableElement = Element & { [BADGE_LIVE_FLAG]?: boolean }

function getGrecaptcha(): GrecaptchaV3 | undefined {
  return (window as unknown as { grecaptcha?: GrecaptchaV3 }).grecaptcha
}

// reCAPTCHA が「今のドキュメントで」使える状態かを判定する。
//
// window.grecaptcha は window 上に残る一方、api.js が document.body に挿入したバッジや
// anchor iframe は Turbo Drive の body 差し替えで丸ごと破棄される（旧 body ごと切り離される）。
// この状態の grecaptcha.execute() は解決も棄却もされないまま固まるため、
// 「window.grecaptcha があること」だけでは初期化済みと判定できない。
//
// さらに、戻る/進むでスナップショットが復元されると iframe ごと複製された
// 「死んだバッジ」が現れる（本番で execute() が9秒経っても解決しないことを確認済み）。
// そのためバッジの有無ではなく、自分で初期化を見届けた実体かどうかで判定する。
function isGrecaptchaReady(): boolean {
  if (getGrecaptcha() === undefined) return false
  const badge = document.querySelector(BADGE_SELECTOR) as FlaggableElement | null
  return badge !== null && badge[BADGE_LIVE_FLAG] === true
}

export default class extends Controller<HTMLFormElement> {
  static values = {
    siteKey: String,
    action: String,
    fieldId: String,
  }

  declare readonly siteKeyValue: string
  declare readonly hasSiteKeyValue: boolean
  declare readonly actionValue: string
  declare readonly fieldIdValue: string

  private apiPromise?: Promise<GrecaptchaV3>
  private apiLoading = false
  private cancelLoad?: () => void
  private connected = false
  private pending = false
  private submitting = false

  connect(): void {
    this.connected = true
    if (!this.enabled) return
    // 送信時のレイテンシを抑えるため、あらかじめ api.js を読み込んでおく。
    // Turbo 遷移直後は reCAPTCHA の DOM が破棄済みのため、ここで再初期化が走る。
    this.loadApi().catch(() => {
      // 読み込み失敗時は送信時にフォールバックするため、ここでは何もしない
    })
  }

  disconnect(): void {
    this.connected = false
    // 送信待ちの状態を持ち越さない。要素が一時的に切断・再接続された場合に
    // pending が立ったままだと、以後の送信をすべて無視してしまう。
    this.pending = false
    // 読み込み待ちのポーリングを後始末する。
    // Turbo 遷移のたびに最大 LOAD_TIMEOUT_MS 分のインターバルが積み残り、
    // 離脱済みのページのために grecaptcha.execute() が走ってしまうのを防ぐ。
    this.cancelLoad?.()
  }

  // フォーム送信をインターセプトし、reCAPTCHA トークンを付与してから送信する。
  // ビュー側で `data-action="submit->recaptcha#execute"` を指定して登録する。
  execute(event: Event): void {
    // reCAPTCHA 無効、またはトークン付与済みの再送信はそのまま通す
    if (!this.enabled || this.submitting) return

    event.preventDefault()
    // トークン取得中の重複送信（連打）は無視する
    if (this.pending) return
    this.pending = true
    // 前回のトークンを消し、今回の取得結果のみを送信する。
    // （取得に失敗した場合に古い/使用済みトークンが再送信されるのを防ぐ）
    this.setField('')

    this.withTimeout(this.fetchToken(), TOKEN_TIMEOUT_MS)
      .then((token) => {
        // 待っている間に Turbo が遷移していたら、離脱済みのフォームに書き込まない
        if (token && this.connected) this.setField(token)
      })
      .catch(() => {
        // トークン取得失敗時も送信は続行する
      })
      .finally(() => {
        this.pending = false
        // 切断済みなら送信しない。遷移後に勝手にフォームが送信されるのを防ぐ。
        if (!this.connected) return
        // submitting は requestSubmit() が同期的に再発火させる submit イベントを
        // 素通しさせるためのフラグ。requestSubmit() 直後に false へ戻すことで、
        // 送信がネットワークエラー等で失敗しフォームが再描画されない経路でも
        // フラグが固定されず、次回送信で必ずトークンを再生成する。
        this.submitting = true
        this.element.requestSubmit()
        this.submitting = false
      })
  }

  private get enabled(): boolean {
    return this.hasSiteKeyValue && this.siteKeyValue !== ''
  }

  // 新しいトークンを取得する
  private fetchToken(): Promise<string> {
    return this.loadApi().then(
      (grecaptcha) =>
        new Promise<string>((resolve, reject) => {
          grecaptcha.ready(() => {
            grecaptcha.execute(this.siteKeyValue, { action: this.actionValue }).then(resolve, reject)
          })
        }),
    )
  }

  // api.js を head に読み込み、reCAPTCHA が今のドキュメントで使える状態になったら解決する。
  // 読み込み失敗はキャッシュせず、次回呼び出しで再試行できるようにする
  // （一時的な読み込み失敗後にハードリロード無しで復旧できるようにするため）。
  private loadApi(): Promise<GrecaptchaV3> {
    if (isGrecaptchaReady()) return Promise.resolve(getGrecaptcha() as GrecaptchaV3)
    // 再利用するのは読み込み中のものだけ。
    // 解決済みのものは、ここに来ている時点で reCAPTCHA が既に使えなくなっており、
    // 返しても内部 DOM を失った個体を渡すだけなので捨てて読み込み直す。
    if (this.apiLoading) return this.apiPromise as Promise<GrecaptchaV3>

    this.apiLoading = true
    this.apiPromise = new Promise<GrecaptchaV3>((resolve, reject) => {
      // script の error はブラウザによっては appendChild 中に同期発火する。
      // その場合でも cleanup() がインターバルを止められるよう、先に組み立てておく。
      let timer: number | undefined
      const cleanup = () => {
        if (timer !== undefined) window.clearInterval(timer)
      }
      const succeed = (grecaptcha: GrecaptchaV3) => {
        cleanup()
        resolve(grecaptcha)
      }
      const fail = (message: string) => {
        cleanup()
        reject(new Error(message))
      }
      // disconnect() から読み込みを打ち切れるようにする
      this.cancelLoad = () => fail('reCAPTCHA の読み込みを中断しました')

      // 過去に追加した api.js は Turbo の head マージで残り続けるため、先に取り除いて
      // 遷移のたびに script タグが積み上がるのを防ぐ（実行済みの効果は取り消されない）。
      document.head.querySelectorAll(`script[src^="${API_SRC}"]`).forEach((el) => el.remove())

      // ここに到達した時点で isGrecaptchaReady() は false、つまり今あるバッジは
      // スナップショット復元で持ち込まれた実体を伴わないクローンである。
      // 残しておくと再初期化後にどちらが実体か判別できなくなるため取り除く。
      document.querySelectorAll(BADGE_SELECTOR).forEach((el) => el.remove())

      // バッジが現れるまでポーリングする。
      // script.onload のタイミングや、既存 script のイベント取りこぼしに依存しない。
      // ここで現れたバッジには「自分で初期化を見届けた実体」の印を付ける。
      // これが isGrecaptchaReady() の判定材料になり、以後スナップショット復元で
      // 持ち込まれたクローンと区別できる。
      //
      // タイムアウトしたら諦めて棄却する（window.grecaptcha が残っていても渡さない）。
      // 内部 DOM を失った個体を渡すと execute() が固まり、結局トークンを得られないまま
      // 待ち時間だけが伸びるため。
      const startedAt = performance.now()
      timer = window.setInterval(() => {
        const grecaptcha = getGrecaptcha()
        const badge = document.querySelector(BADGE_SELECTOR) as FlaggableElement | null
        if (grecaptcha && badge) {
          badge[BADGE_LIVE_FLAG] = true
          succeed(grecaptcha)
        } else if (performance.now() - startedAt > LOAD_TIMEOUT_MS) {
          fail('reCAPTCHA の読み込みがタイムアウトしました')
        }
      }, 100)

      // api.js を追加する。既存 script が読み込み失敗している場合と、
      // Turbo 遷移で内部 DOM が破棄されている場合の両方があるため毎回新しく追加し、
      // reCAPTCHA に初期化をやり直させる（使える状態なら上の isGrecaptchaReady() で解決済み）。
      const script = document.createElement('script')
      script.src = `${API_SRC}?render=${encodeURIComponent(this.siteKeyValue)}`
      script.async = true
      script.defer = true
      script.addEventListener('error', () => fail('reCAPTCHA api.js の読み込みに失敗しました'), { once: true })
      document.head.appendChild(script)
    })

    this.apiPromise
      .catch(() => {
        // 失敗はキャッシュしない（恒久的な不能状態を防ぎ、次回送信で再試行させる）
        this.apiPromise = undefined
      })
      .finally(() => {
        this.apiLoading = false
        this.cancelLoad = undefined
      })

    return this.apiPromise
  }

  // promise がタイムアウトした場合は undefined で解決する（送信をブロックし続けない）
  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
    // タイムアウトが先に解決した後に promise が遅れて reject しても
    // unhandled rejection にならないよう、あらかじめ捕捉しておく
    promise.catch(() => {})
    return Promise.race([
      promise,
      new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), ms)),
    ])
  }

  // hidden input はこのフォームの中に描画されるため、フォーム配下から探す。
  // document.getElementById だと、Turbo で復元された同じ id を持つ別フォームへ
  // 書き込んでしまう可能性がある。
  private setField(token: string): void {
    const field = this.element.querySelector(`#${CSS.escape(this.fieldIdValue)}`)
    if (field instanceof HTMLInputElement) field.value = token
  }
}
